/*global DSV */

/** 
 * DSV Search Result ( Search Page )
 * The functionality is semi-tightly coupled up with Ankiro.
 * 
 */

(function (window) {
    "use strict";

    DSV.ui.searchResult = {

        DOM: {
            container: DSV.DOM.searchResult[0],
            input: document.querySelector('.search-result__input'),
            submitButton: document.querySelector('.search-result__button'),
            resultCount: document.querySelector('.search-result-count span'),
            form: document.querySelector('.search-result__search-container'),
            paginationContainer: document.querySelector('.search-result__pagination'),
            resultListView: document.querySelector('.search-result__result-list'),
            groupSummationsContainer: document.querySelector('.search-result .tabs'),
            groupSummationsTabs: document.querySelectorAll('.search-result .tabs li'),
            sortContainer: document.querySelector('.search-result__sort'),
            sorting: document.querySelector('.search-result__list-sort'),
            noResultsContainer: document.querySelector('.search-result__no-result'),
            bannerContainer: document.querySelector('.search-result__banners')
        },

        DATASET: null,
        
        state: {
            input: "",
            totaltResults: "",
            documents: [],
            startIndex: 1,
            params: null, // Initial parameter. Is updated when user interacts.
            currentTabs: new Map(),
            initialSearchHasBeenMade: false,
        },

        /**
         * This function returns the URL we'll fetch the results from 
         * and handles the insertion of params and query returned from updateQueryString
         * @param {string} params - the params seperated by &, a ? is always appended beforehand
         * @param {string} query - The search query
         */
        getUrl: function(params, query) {
            const baseUrl = `${this.DATASET.searchUrl}${this.DATASET.searchProfile}/${this.DATASET.search}`;

            return `${baseUrl}${params ? `?${params.join('&')}` : `?q=${query}`}`;
        },

        /**
         * Updates the result count in the DOM and state
         * @param {int} amount - The amount to replace with
         */
        updateResultCount: function(amount) {
            this.state.totaltResults = amount;
            this.DOM.resultCount.innerText = amount;

            if (amount === 0) {
                this.DOM.noResultsContainer.classList.remove('hidden')
                this.DOM.sortContainer.classList.add('hidden')
            } else {
                this.DOM.noResultsContainer.classList.add('hidden')
                this.DOM.sortContainer.classList.remove('hidden')
            }
        },

        /**
         * This function groups a list by a key
         * @param {array} list - The array of objects
         * @param {func} keyGetter - The function to access the wanted property in the object looped through
         * @returns {Map} - Returns a Map with grouped values
         */
        groupBy: (list, keyGetter) => {
            const map = new Map();

            list.forEach(item => {
                let keyToGroupBy = keyGetter(item);
    
                const group = map.get(keyToGroupBy);
    
                if (!group) {
                    map.set(keyToGroupBy, [item]);
                } else {
                    group.push(item);
                }
            });
    
            return map;
        },

        /**
         * Handles rendering of the documents returned from Ankiro
         * @param {obj} documents - The documents returned from Ankiro
         */
        handleUpdatingList: function(results) {
            if (!results.Documents) return;
            const {Documents: documents, SearchId: searchId} = results;
            const baseUrl = `${this.DATASET.searchUrl}${this.DATASET.searchProfile}/${this.DATASET.redir}`;

            const listView = documents.map(document => {
                // Ankiro returns anonymous objects.
                // We want to access them by the key in the template so we pick them out and throw them in a map.
                const mappedProperties = this.groupBy(document.Properties, property => property.Name);
                return DSV.template.listViewTemplate.getTemplate(mappedProperties, document, searchId, baseUrl);
            }).join('');

            this.DOM.resultListView.innerHTML = listView;

            [...this.DOM.resultListView.querySelectorAll('li')].forEach((item, index) => {
                TweenLite.to(item, .5, {delay: index * .15, opacity: 1});
            })
        },

        /**
         * Handles the conducting of pagination buttons
         * @param {obj} paginationObject - Object of pagination data from Ankiro
         */
        handlePaginationButtons: function(paginationObject) {
            if (!paginationObject) return;
            const { Pages: pages, CurrentPage: currentPage, TotalPages: totalPages } = paginationObject;
            this.createPaginationButtons(pages, currentPage, totalPages);
        },

        /**
         * Handles the change of the startIndex e.g. in the event of a pagination change
         * @param {int} startIndex - startIndex to let Ankiro know where we want the results to begin
         */
        handleNewStartIndex: function(startIndex) {
            this.state.startIndex = startIndex;
            this.handleQueryParameters("startIndex", startIndex);
        },

        handleSearchBanner: function(result) {
            if (!result) return this.DOM.bannerContainer.innerHTML = "";

            if (result.length > 0) {
                const banners = result.map(banner => {
                    return DSV.template.bannerTemplate.getTemplate(banner);
                }).join('');

                this.DOM.bannerContainer.innerHTML = banners;
            } else {
                // Delete banner
                this.DOM.bannerContainer.innerHTML = "";
            }
        },

        /**
         * Handles the creation and rendering of the pagination buttons
         * @param {array} pages - The Pages array from returned from Ankiro
         * @param {int} currentPage - Current page integer also from Ankiro
         * @param {int} totalPAges - total page integer also from Ankiro
         */
        createPaginationButtons: function(pages, currentPage, totalPages) {
            const buttonAmount = pages.length;
            const paginationButtons = pages.map((page, index) => DSV.template.paginationButton.getTemplate(page, currentPage, index, buttonAmount, totalPages))
                .join('');

            this.DOM.paginationContainer.innerHTML = paginationButtons;
        },

        /**
         * Handles the event of clicking on a sorting option
         * @param {Event} e 
         * @param {HTMLElement} chosenTarget - An optional parameter if no event is passed
         */
        handleSorting: function(e, chosenTarget = null) {
            const { target } = e || {};

            if ((target && !target.closest('.search-result__sort-item')) && !chosenTarget) return;
            const sortBy = (target && target.dataset.sortBy) || (chosenTarget && chosenTarget.dataset.sortBy);
            const sortSelections = this.DOM.sorting.querySelectorAll('a');

            sortSelections.forEach(sortSelection => {
                if (sortSelection !== target && sortSelection !== chosenTarget) {
                    sortSelection.classList.remove('selected');
                    return;
                }

                sortSelection.classList.add('selected');
            });

            this.handleQueryParameters("startIndex", 1);
            this.handleQueryParameters("sortDesc", sortBy);
            this.fetchResult();
        },

        /**
         * Handles the event of clicking on a tab
         * @param {Event} e 
         */
        handleTabClick: function(e) {
            const { target } = e;
            e.preventDefault();
            const tab = target.closest('li');

            if (!tab || tab.classList.contains('selected')) return;

            this.handleQueryParameters("startIndex", 1);
            this.state.startIndex = 1;

            const currentTabParameters = [...this.currentTabs.keys()];
            // Delete any tab parameters already set so we don't have any leftovers from last tab click
            currentTabParameters.forEach(parameter => {
                const newParameters = DSV.tools.updateQueryString(null, parameter, null);
                this.state.params = newParameters.params;
                this.replaceUrlState(newParameters.href);
                this.currentTabs.delete(parameter);
            });

            // Handle the insertion of the query parameters provided by the tab
            const parsedData = JSON.parse(target.closest('li').dataset.searchParamValue);
            const entries = Object.entries(parsedData);
            entries.forEach(entry => {
                const [key, value] = entry;
                this.currentTabs.set(key, value);
                this.handleQueryParameters(key, value);
            });

            this.fetchResult();
        },

        /**
         * Handles updating the number of results found mapped to the search tabs.
         * This function expects the tabs to align with the PreFacetInfo from Ankiro to work.
         * @param {Array} groupSummationsArray - the PreFacetInfo array from Ankiro
         */
        updateGroupSummations: function(groupSummationsArray) {
            const [ groupSummations ] = groupSummationsArray;
            const groups = this.groupBy( groupSummations.Groups, group => group.Value);

            [...this.DOM.groupSummationsTabs].forEach(tab => {
                const tabDomain = JSON.parse(tab.dataset.searchParamValue);
                const entries = Object.entries(tabDomain);
                
                entries.forEach(entry => {
                    const [key, value] = entry;

                    if (key === "Domain") {
                        const mappedGroup = groups.get(value)
                        const countDOM = tab.querySelector('.search-result__tab-count');

                        // Handle active class to set it on load
                        if (DSV.tools.getUrlParams("Domain") === value) {
                            tab.classList.add('active')
                        } else {
                            tab.classList.remove('active')
                        }

                        if (mappedGroup === undefined) {
                            countDOM.innerText = 0;
                            return;
                        };
                        const { Count: count } = mappedGroup[0];
                        countDOM.innerText = count;
                    }
                })
            });
        },

        /**
         * Handles replacing the URL state. Used for updating the URL without navigation.
         * @param {string} newUrl - The url that the current url will be replaced with
         */
        replaceUrlState: function(newUrl) {
            window.history.replaceState('', '', newUrl);
        },

        /**
         * This big fella' takes care of conducting all the different functions
         * that needs to be run after a search.
         * @param {obj} result - The result object returned from the fetch to Ankiro
         */
        conductSearchResult: function(result) {
            const paginationResult = result.Decorations && result.Decorations.Pagination
            const banners = result.Decorations && result.Decorations.Banners;

            this.state.documents = result.Documents;

            this.updateResultCount(result.TotalResults);
            this.handlePaginationButtons(paginationResult);
            this.handleUpdatingList(result);
            if (result.PreFacetInfo) this.updateGroupSummations(result.PreFacetInfo.GroupSummations);
            this.handleSearchBanner(banners);
        },

        /**
         * Handles updating or deleting query parameters and replaces the URL state with the result.
         * @param {string} parameter - The parameter to be updated, set or deleted
         * @param {string} value - The value for the parameter - null will delete the parameter
         * @returns {object} - Returns an object with the current query parameters and the whole href with parameters
         */
        handleQueryParameters: function(parameter, value) {
            const URLWithUpdatedQueryParam = DSV.tools.updateQueryString(null, parameter, value);
            this.state.params = URLWithUpdatedQueryParam.queries;
            this.replaceUrlState(URLWithUpdatedQueryParam.href);

            return URLWithUpdatedQueryParam;
        },

        /**
         * Fetches the result according to input and startIndex state
         */
        fetchResult: function() {
            if (!this.state.initialSearchHasBeenMade) {
                this.state.initialSearchHasBeenMade = true;
                this.initialQueryParams();
            }

            this.handleQueryParameters("q", this.state.input);

            this.DOM.groupSummationsContainer.classList.remove('hidden');

            fetch(this.getUrl(this.state.params, this.state.input))
                .then(result => result.json())
                .then(result => {
                    this.conductSearchResult(result);
                });
        },

        initialQueryParams: function() {

            const startIndexOnLoad = DSV.tools.getUrlParams('startIndex');
            if (
                startIndexOnLoad !== 0 &&
                startIndexOnLoad.length !== 0
            ) {
                this.state.startIndex = DSV.tools.getUrlParams('startIndex');
            }

            const sortOnLoad = DSV.tools.getUrlParams('sortDesc');
            if (sortOnLoad !== 0 &&
                sortOnLoad.length !== 0) {
                const sortElement = document.querySelector(`.search-result__sort-item[data-sort-by="${sortOnLoad}"]`);
                this.handleSorting(null, sortElement);
            }

            // Handle no Domain is set on first access
            const domainOnLoad = DSV.tools.getUrlParams('Domain');
            if (domainOnLoad === 0) {
                // Set the Domain Query Params to the first tab that is found that is not hidden
                let domainFound = false;
                [...this.DOM.groupSummationsTabs].forEach(tab => {
                    if (tab.classList.contains('hidden') || domainFound) return;
                    
                    const tabDomain = JSON.parse(tab.dataset.searchParamValue);
                    const entries = Object.entries(tabDomain);
                    
                    entries.forEach(entry => {
                        if (domainFound) return;
                        const [key, value] = entry;
    
                        if (key === "Domain") {
                            domainFound = true;

                            this.handleQueryParameters(key, value);
                        }
                    })
                })
            }

            // Handle appending regionLang on load
            try {   
                const searchRegionLanguageData = JSON.parse(this.DATASET.searchRegionLanguage)
                const [ regionLangParameter ] = Object.keys(searchRegionLanguageData);
                const [ regionLangValue ] = Object.values(searchRegionLanguageData);
                this.handleQueryParameters(regionLangParameter, regionLangValue);
            } catch(e) {
                window.console.warn('regionLang not set', e)
            }

            // Handle appending Country on load
            try {   
                const searchRegionData = JSON.parse(this.DATASET.searchRegion)
                const [ regionParameter ] = Object.keys(searchRegionData);
                const [ regionValue ] = Object.values(searchRegionData);
                this.handleQueryParameters(regionParameter, regionValue);
            } catch(e) {
                window.console.warn('regionLang not set', e)
            }
        },

        /**
         * This is where the initial setup happens
         */
        init: function() {
            this.DATASET = this.DOM.container.dataset;
            this.currentTabs = new Map();

            // Prevent a crash if no query is set upon loading
            // And don't do anything if the query is already a star selector when loading
            const queryOnLoad = DSV.tools.getUrlParams('q');

            if (
              queryOnLoad !== 0 &&
              queryOnLoad.length !== 0
            ) {
                this.state.input = decodeURIComponent(queryOnLoad);
                this.DOM.input.value = this.state.input;
            }

            if (this.state.input) {
                this.fetchResult();
                this.state.initialSearchHasBeenMade = true;
            }

            // * Event listeners
            this.DOM.input.addEventListener('input', e => {
                const { target } = e;
                this.state.input = target.value.trim();
            });

            this.DOM.form.addEventListener('submit', e => {
                e.preventDefault();

                // Search for everything if none is input
                if (this.state.input.length === 0) {
                    this.state.input = "*";
                }

                //check if search value matches Track number pattern
                if (DSV.ui.checkForTrackNumber(this.state.input) && DSV.data.overlayEndpoint) {
                    DSV.ui.overlays.loadContent(`${DSV.data.overlayEndpoint}?id=${this.state.input}`);
                } else {
                    this.fetchResult();
                }
            });

            this.DOM.paginationContainer.addEventListener('click', e => {
                e.preventDefault();
                const { target } = e;
                const paginationButton = target.closest('.pagination__item')

                if (!paginationButton) return;

                this.handleNewStartIndex(paginationButton.dataset.paginationOffset);

                TweenLite.to(window, 1, {scrollTo:{y:this.DOM.sortContainer, offsetY: 150}});
                
                this.fetchResult();
            });

            this.DOM.groupSummationsContainer.addEventListener('click', e => {
                this.handleTabClick(e);
            });

            this.DOM.sorting.addEventListener('click', e => {
                e.preventDefault();
                this.handleSorting(e);
            });
        }
    };

})(window);